import { EVAL_CSV_REQUIRED_COLUMNS } from "../constants";

/**
 * Splits CSV content into rows, honouring quoted cells: an expected answer may itself contain
 * commas and line breaks, so the content can't simply be split on "\n".
 *
 * @param {string} csv - Raw CSV content.
 * @returns {Array<string>} The non-empty rows, in order.
 */
const splitCsvRows = (csv: string): string[] => {
  const rows: string[] = [];
  let currentRow = "";
  let isQuoted = false;

  for (const char of csv) {
    if (char === '"') {
      isQuoted = !isQuoted;
    }

    if (char === "\n" && !isQuoted) {
      rows.push(currentRow);
      currentRow = "";
      continue;
    }

    currentRow += char;
  }

  rows.push(currentRow);

  return rows.map((row) => row.trim()).filter(Boolean);
};

/**
 * Reads the header row's column names. Cells may be quoted, as they are in the committed case sets.
 *
 * @param {string} headerRow - The first row of the CSV.
 * @returns {Array<string>} The column names.
 */
const parseHeaderColumns = (headerRow: string): string[] =>
  headerRow.split(",").map((column) => column.trim().replace(/^"|"$/g, ""));

export interface ParsedEvalCsv {
  caseCount: number;
  error: string;
}

/**
 * Validates the shape of an uploaded case CSV the way the API's own reader does — by column name,
 * not by a literal header string — and counts the cases it holds.
 *
 * @param {string} csv - Raw CSV content, possibly starting with a byte order mark.
 * @returns {ParsedEvalCsv} The case count, or the reason the file can't be run.
 */
export const parseEvalCsv = (csv: string): ParsedEvalCsv => {
  const rows = splitCsvRows(csv.replace(/^\uFEFF/, ""));
  const [headerRow, ...caseRows] = rows;

  if (!headerRow) {
    return { caseCount: 0, error: "The file is empty." };
  }

  const columns = parseHeaderColumns(headerRow);
  const missingColumns = EVAL_CSV_REQUIRED_COLUMNS.filter((column) => !columns.includes(column));

  if (missingColumns.length) {
    return {
      caseCount: 0,
      error: `CSV is missing the ${missingColumns.join(" and ")} column${
        missingColumns.length === 1 ? "" : "s"
      }. Download the template for the correct format.`,
    };
  }

  if (!caseRows.length) {
    return { caseCount: 0, error: "CSV must contain at least one test case row." };
  }

  return { caseCount: caseRows.length, error: "" };
};
